'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useCallbackRef } from '@skin-seoul/utils';
import { Box, Flex, Text, Heading, Button, Menu, useBreakpointValue } from '@skin-seoul/design-system';
import { useSearchParams } from 'next/navigation';
import LucideIcon from '@/components/ui/LucideIcon';
import { QueryBoundary } from '@/components/ui/QueryBoundary';
import FilterSidebarSkeleton from '@/components/ui/FilterSidebarSkeleton';
import FilterSidebar from '../FilterSidebar';
import MobileFilterButton from '../MobileFilterButton';
import MobileFilterDrawer from '../MobileFilterDrawer';
import MobileSortButton from '../MobileSortButton';
import MobileSortDrawer from '../MobileSortDrawer';
import ProductsGridSection from './ProductsGridSection';
import PaginationSection from './PaginationSection';
import { SORT_OPTIONS, DEFAULT_SORT_VALUE, DEFAULT_PAGE } from '../../constants/sortOptions';
import { getPageTitle, getSortLabel } from '../../utils/titleHelpers';
import { type FilterOptions, type FilterSummary } from '../../types/filters';
import { parseFilterUrlString, buildFilterUrlString, updateFilterUrl, type FilterUrlState } from '../../utils/filterUrlUtils';
import CollectionIntroductionSection from './CollectionIntroductionSection';
import SubSubcategoriesSection from '../SubSubcategoriesSection';
import type { TaxonomyCategory } from '../../utils/categoryUtils';
import { CLASSNAME, type CollectionIntroduction, type CollectionTerm, type ProductSortBy } from '@skin-seoul/service';
import { useProductAccumulation } from '../../hooks/useProductAccumulation';
interface FiltersSectionProps {
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  productTag?: string;
  searchQuery?: string;
  collectionName?: string;
  productCount?: number;
  introduction?: CollectionIntroduction;
  collectionTerm?: CollectionTerm;
  hasIntroductionSection?: boolean;
  hidePageTitle?: boolean;
  hideAllFilters?: boolean;
  hideSortBy?: boolean;
  children?: React.ReactNode;
  categories?: TaxonomyCategory[];
  initialFilterSummary?: FilterSummary;
}
interface PageTitleProps {
  category?: string;
  subcategory?: string;
  brand?: string;
  productTag?: string;
  searchQuery?: string;
  filterSummary?: FilterSummary;
  filters: FilterOptions;
  sortBy: ProductSortBy;
  collectionTerm?: CollectionTerm;
  isDesktop?: boolean;
}
const PageTitle = ({
  category,
  subcategory,
  brand,
  productTag,
  searchQuery,
  filterSummary,
  filters,
  sortBy,
  collectionTerm,
  isDesktop = true
}: PageTitleProps) => {
  const title = getPageTitle({
    searchQuery,
    collectionTerm,
    category,
    subcategory,
    brand,
    productTag,
    filterSummary,
    filters,
    sortBy
  });
  return <Heading as="h3" textAlign="left" textStyle={isDesktop ? 'heading.h3' : 'heading.h4'} fontWeight="medium" textTransform="capitalize" color="neutral.700" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Heading" data-sentry-component="PageTitle" data-sentry-source-file="FiltersSection.tsx">
      {title}
    </Heading>;
};
interface ResultsHeaderProps {
  productCount: number;
  sortBy: ProductSortBy;
  onSortChange: (value: string) => void;
  onMobileSortOpen?: () => void;
  hideSortBy?: boolean;
}
const ResultsHeader = ({
  productCount,
  sortBy,
  onSortChange,
  onMobileSortOpen,
  hideSortBy = false
}: ResultsHeaderProps) => <Flex justify="space-between" align="center" marginBottom={{
  base: 2,
  tablet: 6
}} paddingY={{
  base: 2,
  tablet: 4
}} flexWrap={{
  base: 'wrap',
  tablet: 'nowrap'
}} gap={3} data-sentry-element="Flex" data-sentry-component="ResultsHeader" data-sentry-source-file="FiltersSection.tsx">
    <Text fontWeight="regular" color="neutral.600" textStyle="label.large" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="FiltersSection.tsx">
      {productCount} results
    </Text>

    {!hideSortBy && <>
        <Box display={{
      base: 'block',
      tablet: 'none'
    }}>
          <MobileSortButton onClick={onMobileSortOpen || (() => undefined)} />
        </Box>

        <Box display={{
      base: 'none',
      tablet: 'block'
    }}>
          <Flex align="center" gap={3}>
            <Flex align="center" gap={2}>
              <LucideIcon name="ArrowDownUp" boxSize={4} color="neutral.600" />
              <Text textStyle="paragraph.medium" color="neutral.600" fontWeight="medium">
                Sort by
              </Text>
            </Flex>
            <Menu.Root>
              <Menu.Trigger asChild>
                <Button variant="outline" size="sm" width="164px" height="44px" padding={3} border="1px solid" borderRadius="8px" borderColor="neutral.100" justifyContent="space-between" _focus={{
              outline: 'none',
              boxShadow: 'none'
            }}>
                  <Text textStyle="label.medium" fontWeight="regular" color="neutral.600">
                    {getSortLabel(sortBy)}
                  </Text>
                  <LucideIcon name="ChevronDown" boxSize={5} color="neutral.600" />
                </Button>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content background="white" border="1px solid" borderColor="neutral.100" borderRadius="4px">
                  {SORT_OPTIONS.map(option => <Menu.Item key={option.value} value={option.value} onClick={() => onSortChange(option.value)} textStyle="label.medium" fontWeight="regular" color="neutral.600" cursor="pointer" _hover={{
                background: 'primary.50'
              }}>
                      {option.label}
                    </Menu.Item>)}
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </Flex>
        </Box>
      </>}
  </Flex>;
interface ProductsGridWithPaginationProps {
  category?: string;
  subcategory?: string;
  subSubcategory?: string;
  brand?: string;
  productTag?: string;
  searchQuery?: string;
  collectionName?: string;
  filters: FilterOptions;
  sortBy: ProductSortBy;
  currentPage: number;
  productsPerPage: number;
  shouldShowPagination: boolean;
  totalPages: number;
  productCount: number;
  onProductCountChange: (count: number) => void;
  onFilterSummaryChange: (summary: FilterSummary | undefined) => void;
  onPageChange: (page: number) => void;
  children?: React.ReactNode;
  isMobile?: boolean;
}
const ProductsGridWithPagination = ({
  category,
  subcategory,
  subSubcategory,
  brand,
  productTag,
  searchQuery,
  collectionName,
  filters,
  sortBy,
  currentPage,
  productsPerPage,
  shouldShowPagination,
  totalPages,
  onProductCountChange,
  onFilterSummaryChange,
  onPageChange,
  children
}: ProductsGridWithPaginationProps) => {
  return <>
      <ProductsGridSection category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} productTag={productTag} searchQuery={searchQuery} collectionName={collectionName} filters={filters} sortBy={sortBy} currentPage={currentPage} productsPerPage={productsPerPage} onProductCountChange={onProductCountChange} onFilterSummaryChange={onFilterSummaryChange} accumulatedProducts={undefined} accumulatedReviewStatsMap={undefined} onProductsLoaded={undefined} data-sentry-element="ProductsGridSection" data-sentry-source-file="FiltersSection.tsx" />

      {shouldShowPagination && <PaginationSection currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />}

      {children}
    </>;
};
const FiltersSection = ({
  category,
  subcategory,
  subSubcategory,
  brand,
  productTag,
  searchQuery,
  collectionName,
  productCount: initialProductCount = 0,
  introduction,
  collectionTerm,
  hasIntroductionSection,
  hidePageTitle,
  hideAllFilters = false,
  hideSortBy = false,
  children,
  categories,
  initialFilterSummary
}: FiltersSectionProps) => {
  const searchParams = useSearchParams();
  const [productCount, setProductCount] = useState(initialProductCount);
  const [sortBy, setSortBy] = useState<ProductSortBy>(DEFAULT_SORT_VALUE as ProductSortBy);
  const {
    filters,
    currentPage
  } = useMemo(() => {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      const parsed = parseFilterUrlString(filtersParam);
      return {
        filters: {
          brands: parsed.brands || [],
          skinTypes: parsed.skinTypes || [],
          skinConcerns: parsed.skinConcerns || [],
          ingredients: parsed.ingredients || [],
          minPrice: parsed.minPrice,
          maxPrice: parsed.maxPrice
        },
        currentPage: parsed.page && parsed.page > 1 ? parsed.page : DEFAULT_PAGE
      };
    }
    return {
      filters: {},
      currentPage: DEFAULT_PAGE
    };
  }, [searchParams]);
  const [localFilters, setLocalFilters] = useState<FilterOptions>({});
  const [localCurrentPage, setLocalCurrentPage] = useState(currentPage);
  const isMobile = useBreakpointValue({
    base: true,
    tablet: false,
    desktop: false
  }) ?? false;
  const productsPerPage = 48;
  const effectiveFilters = Object.keys(filters).length > 0 ? filters : localFilters;
  const effectiveCurrentPage = (() => {
    if (searchParams.get('filters')) {
      return currentPage;
    }
    return localCurrentPage;
  })();
  const [filterSummary, setFilterSummary] = useState<FilterSummary | undefined>(initialFilterSummary);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);
  const previousFiltersRef = useRef<FilterOptions>({});
  const {
    accumulatedProducts: _accumulatedProducts,
    accumulatedReviewStatsMap: _accumulatedReviewStatsMap,
    isLoadingMore: _isLoadingMore,
    handleProductsLoaded: _handleProductsLoaded,
    handleShowMore: _handleShowMoreFromHook,
    setIsLoadingMore: _setIsLoadingMore,
    resetAccumulation: _resetAccumulation
  } = useProductAccumulation({
    isMobile: false,
    // Disable accumulation - now using pagination on mobile
    effectiveFilters,
    sortBy,
    effectiveCurrentPage
  });
  const totalPages = Math.max(1, Math.ceil(productCount / productsPerPage));
  const shouldShowPagination = productCount > productsPerPage;
  const scrollToTop = useCallbackRef(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
  const updatePageInUrl = useCallback((page: number) => {
    const filtersParam = searchParams.get('filters');
    const currentState = filtersParam ? parseFilterUrlString(filtersParam) : {
      brands: [],
      skinTypes: [],
      skinConcerns: [],
      ingredients: []
    };
    const newState: FilterUrlState = {
      ...currentState,
      page: page > 1 ? page : undefined
    };
    const filterString = buildFilterUrlString(newState);
    updateFilterUrl(filterString);
  }, [searchParams]);
  const filterParamsNeedScrollup = useCallback((newFilters: FilterOptions, previousFilters: FilterOptions) => {
    const priceKeys = ['minPrice', 'maxPrice'] as const;
    const nonPriceKeys = ['categories', 'brands', 'skinTypes', 'skinConcerns', 'ingredients'] as const;
    const nonPriceChanged = nonPriceKeys.some(key => {
      const newValue = newFilters[key];
      const prevValue = previousFilters[key];
      if (Array.isArray(newValue) && Array.isArray(prevValue)) {
        return newValue.length !== prevValue.length || newValue.some((val, idx) => val !== prevValue[idx]);
      }
      return newValue !== prevValue;
    });
    const priceChanged = priceKeys.some(key => newFilters[key] !== previousFilters[key]);
    return priceChanged && !nonPriceChanged;
  }, []);
  const handleFilterChange = useCallback((newFilters: FilterOptions) => {
    const shouldScrollToTop = !filterParamsNeedScrollup(newFilters, previousFiltersRef.current);
    setLocalFilters(newFilters);
    setLocalCurrentPage(DEFAULT_PAGE);
    if (shouldScrollToTop) {
      scrollToTop();
    }
    previousFiltersRef.current = {
      ...newFilters
    };
  }, [scrollToTop, filterParamsNeedScrollup]);
  const handleSortChange = useCallbackRef((value: string) => {
    setSortBy(value as ProductSortBy);
    setLocalCurrentPage(DEFAULT_PAGE);
  });
  const handleProductCountChange = useCallbackRef((count: number) => {
    setProductCount(count);
  });
  const handlePageChange = useCallback((page: number) => {
    setLocalCurrentPage(page);
    updatePageInUrl(page);
    scrollToTop();
  }, [scrollToTop, updatePageInUrl, setLocalCurrentPage]);
  const _handleShowMore = useCallback(() => {
    if (effectiveCurrentPage < totalPages) {
      _handleShowMoreFromHook();
      setLocalCurrentPage(effectiveCurrentPage + 1);
    }
  }, [effectiveCurrentPage, totalPages, _handleShowMoreFromHook]);
  const handleFilterSummaryChange = useCallbackRef((summary: FilterSummary | undefined) => {
    setFilterSummary(summary);
  });
  const handleMobileFilterOpen = useCallbackRef(() => {
    setIsMobileFilterOpen(true);
  });
  const handleMobileFilterClose = useCallbackRef(() => {
    setIsMobileFilterOpen(false);
  });
  const handleMobileSortOpen = useCallbackRef(() => {
    setIsMobileSortOpen(true);
  });
  const handleMobileSortClose = useCallbackRef(() => {
    setIsMobileSortOpen(false);
  });
  useEffect(() => {
    if (effectiveCurrentPage > totalPages && totalPages > 0) {
      setLocalCurrentPage(1);
    }
  }, [effectiveCurrentPage, totalPages]);
  return <Box width="100%" data-sentry-element="Box" data-sentry-component="FiltersSection" data-sentry-source-file="FiltersSection.tsx">
      <Box display={{
      base: 'none',
      tablet: 'block'
    }} width="100%" data-sentry-element="Box" data-sentry-source-file="FiltersSection.tsx">
        <Box width="100%" paddingTop={8} marginBottom={8} data-sentry-element="Box" data-sentry-source-file="FiltersSection.tsx">
          {!hasIntroductionSection && !hidePageTitle && <PageTitle category={category} subcategory={subcategory} brand={brand} productTag={productTag} searchQuery={searchQuery} filterSummary={filterSummary} filters={brand ? effectiveFilters : {
          ...effectiveFilters,
          brands: []
        }} sortBy={sortBy} collectionTerm={collectionTerm} isDesktop={true} />}
          {!hasIntroductionSection && <CollectionIntroductionSection introduction={introduction} />}
        </Box>

        <Flex gap={6} align="flex-start" width="100%" overflow="hidden" data-sentry-element="Flex" data-sentry-source-file="FiltersSection.tsx">
          <QueryBoundary loadingFallback={<FilterSidebarSkeleton />} data-sentry-element="QueryBoundary" data-sentry-source-file="FiltersSection.tsx">
            <FilterSidebar onFilterChange={handleFilterChange} onSortByChange={handleSortChange} category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} filterSummary={filterSummary} sortBy={sortBy} productCount={productCount} hideCategoryFilter={hasIntroductionSection} hideAllFilters={hideAllFilters} categories={categories} data-sentry-element="FilterSidebar" data-sentry-source-file="FiltersSection.tsx" />
          </QueryBoundary>

          <Box flex="1" minWidth={0} overflow="hidden" marginTop={4} data-sentry-element="Box" data-sentry-source-file="FiltersSection.tsx">
            <ResultsHeader productCount={productCount} sortBy={sortBy} onSortChange={handleSortChange} onMobileSortOpen={handleMobileSortOpen} hideSortBy={hideSortBy} data-sentry-element="ResultsHeader" data-sentry-source-file="FiltersSection.tsx" />

            <ProductsGridWithPagination category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} productTag={productTag} searchQuery={searchQuery} collectionName={collectionName} filters={effectiveFilters} sortBy={sortBy} currentPage={effectiveCurrentPage} productsPerPage={productsPerPage} shouldShowPagination={shouldShowPagination} totalPages={totalPages} productCount={productCount} onProductCountChange={handleProductCountChange} onFilterSummaryChange={handleFilterSummaryChange} onPageChange={handlePageChange} isMobile={false} data-sentry-element="ProductsGridWithPagination" data-sentry-source-file="FiltersSection.tsx">
              {children}
            </ProductsGridWithPagination>
          </Box>
        </Flex>
      </Box>

      <Box display={{
      base: 'block',
      tablet: 'none'
    }} width="100%" data-sentry-element="Box" data-sentry-source-file="FiltersSection.tsx">
        <Box width="100%" paddingTop={6} data-sentry-element="Box" data-sentry-source-file="FiltersSection.tsx">
          {!hasIntroductionSection && !hidePageTitle && <Box marginBottom={6}>
              <PageTitle category={category} subcategory={subcategory} brand={brand} productTag={productTag} searchQuery={searchQuery} filterSummary={filterSummary} filters={brand ? effectiveFilters : {
            ...effectiveFilters,
            brands: []
          }} sortBy={sortBy} collectionTerm={collectionTerm} isDesktop={false} />
            </Box>}
          {!hasIntroductionSection && <CollectionIntroductionSection introduction={introduction} />}

          <SubSubcategoriesSection subcategory={subcategory} subSubcategory={subSubcategory} data-sentry-element="SubSubcategoriesSection" data-sentry-source-file="FiltersSection.tsx" />
        </Box>

        <Text fontWeight="regular" className={CLASSNAME.NOT_TRANSLATE} color="neutral.600" textStyle="paragraph.xsmall" marginBottom={4} textAlign="left" data-sentry-element="Text" data-sentry-source-file="FiltersSection.tsx">
          {productCount} results
        </Text>

        <Flex gap={3} marginBottom={4} width="100%" data-sentry-element="Flex" data-sentry-source-file="FiltersSection.tsx">
          <Flex flex="1" data-sentry-element="Flex" data-sentry-source-file="FiltersSection.tsx">
            <MobileFilterButton onClick={handleMobileFilterOpen} data-sentry-element="MobileFilterButton" data-sentry-source-file="FiltersSection.tsx" />
          </Flex>
          {!hideSortBy && <Flex flex="1">
              <MobileSortButton onClick={handleMobileSortOpen} />
            </Flex>}
        </Flex>

        <ProductsGridWithPagination category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} productTag={productTag} searchQuery={searchQuery} collectionName={collectionName} filters={effectiveFilters} sortBy={sortBy} currentPage={effectiveCurrentPage} productsPerPage={productsPerPage} shouldShowPagination={shouldShowPagination} totalPages={totalPages} productCount={productCount} onProductCountChange={handleProductCountChange} onFilterSummaryChange={handleFilterSummaryChange} onPageChange={handlePageChange} isMobile={isMobile} data-sentry-element="ProductsGridWithPagination" data-sentry-source-file="FiltersSection.tsx">
          {children}
        </ProductsGridWithPagination>

        <MobileFilterDrawer isOpen={isMobileFilterOpen} onClose={handleMobileFilterClose} onFilterChange={handleFilterChange} onSortByChange={handleSortChange} category={category} subcategory={subcategory} subSubcategory={subSubcategory} brand={brand} filterSummary={filterSummary} sortBy={sortBy} productCount={productCount} hideCategoryFilter={hasIntroductionSection} hideAllFilters={hideAllFilters} categories={categories} data-sentry-element="MobileFilterDrawer" data-sentry-source-file="FiltersSection.tsx" />

        {!hideSortBy && <MobileSortDrawer isOpen={isMobileSortOpen} onClose={handleMobileSortClose} onSortByChange={handleSortChange} sortBy={sortBy} />}
      </Box>
    </Box>;
};
export default FiltersSection;